// src/components/ProductCard.jsx
import React, { useEffect, useState } from 'react';
import { getUnitPriceFromTiers, normalizeAndValidateTiers } from '../utils/moqUtils';
import { useApp } from '../context/AppContext';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Star, ShoppingCart } from 'lucide-react';

const ProductCard = ({ product }) => {
  const { user, addToCart, openAuthModal } = useApp();
  const navigate = useNavigate();

  const [avgRating, setAvgRating] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  // ✅ 댓글(리뷰)에서 평균 별점 계산
  useEffect(() => {
    const q = query(collection(db, 'comments'), where('productId', '==', product.id));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const comments = snapshot.docs.map(doc => doc.data());
      if (comments.length > 0) {
        const sum = comments.reduce((acc, c) => acc + c.rating, 0);
        setAvgRating((sum / comments.length).toFixed(1));
      } else {
        setAvgRating(0);
      }
    });
    return () => unsubscribe();
  }, [product.id]);

  // ✅ 주문 수는 일반적으로 별도 필드로 관리 (예: product.orderCount)
  // 여기선 product.orderCount 사용 (업로드 시 초기화 가능)
  useEffect(() => {
    setTotalOrders(product.orderCount || 0);
  }, [product.orderCount]);

  // handleAddToCart 함수는 현재 사용되지 않지만 향후 사용을 위해 유지
  // const handleAddToCart = () => {
  //   if (!user) {
  //     alert('Please sign in to add items to cart.');
  //     return;
  //   }
  //   const minQty = product.moqPrices[0]?.minQty || 1;
  //   addToCart(product, minQty);
  //   navigate('/cart');
  // };

  const handleAddToCart = async () => {
    if (!user) {
      // 비회원의 경우 로그인 모달 열기
      openAuthModal('buyer', 'login');
      return;
    }

    if (isAddingToCart) return; // 중복 클릭 방지

    setIsAddingToCart(true);
    
    try {
      // 최소 주문 수량으로 카트에 추가
      const minQty = (product.moqPrices && product.moqPrices.length > 0) 
        ? product.moqPrices[0]?.minQty || 1 
        : 1;
      await addToCart(product, minQty);
      
      // 성공 피드백
      setAddedToCart(true);
      
      // 토스트 알림 표시
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-slide-down';
      toast.textContent = 'Added to cart!';
      document.body.appendChild(toast);
      
      // 2초 후 토스트 제거
      setTimeout(() => {
        toast.remove();
      }, 2000);
      
      // 2초 후 카트 페이지로 이동
      setTimeout(() => {
        navigate('/cart');
      }, 2000);
      
    } catch (error) {
      console.error('Failed to add to cart:', error);
      
      // 에러 토스트 표시 (더 자세한 정보)
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-slide-down';
      toast.textContent = `Failed to add to cart: ${error.message || 'Unknown error'}`;
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.remove();
      }, 5000);
    } finally {
      setIsAddingToCart(false);
    }
  };

  // MOQ tiers 정규화 및 안전한 접근
  let unitPrice = 0;
  let lowestPrice = 0;
  
  // moqPrices가 존재하고 유효한지 확인
  if (!product.moqPrices || !Array.isArray(product.moqPrices) || product.moqPrices.length === 0) {
    console.error('Invalid moqPrices for product:', product);
    unitPrice = 0;
    lowestPrice = 0;
  } else {
    try {
      const normalizedTiers = normalizeAndValidateTiers(
        product.moqPrices.map(tier => ({
          minQty: tier.minQty,
          maxQty: tier.maxQty || (tier.minQty + 999),
          unitPrice: tier.unitPrice || tier.price
        }))
      );
      unitPrice = getUnitPriceFromTiers(normalizedTiers, 1);
      lowestPrice = Math.min(...normalizedTiers.map(p => p.unitPrice));
    } catch (error) {
      console.error('Error processing MOQ tiers for product:', product, error);
      // 기본값 설정 - 안전한 접근
      if (product.moqPrices && product.moqPrices.length > 0) {
        unitPrice = product.moqPrices[0]?.unitPrice || product.moqPrices[0]?.price || 0;
        lowestPrice = Math.min(...product.moqPrices.map(p => p.unitPrice || p.price || 0));
      } else {
        unitPrice = 0;
        lowestPrice = 0;
      }
    }
  }

  return (
    <div className="bg-apple-bg rounded-apple border border-apple-divider hover:shadow-apple transition-all duration-300 overflow-hidden h-full flex flex-col touch-manipulation apple-hover">
      <div className="h-24 sm:h-32 md:h-36 overflow-hidden">
        <img 
          src={(product.images && product.images.length > 0) ? product.images[0] : '/placeholder-image.svg'} 
          alt={product.name || 'Product'}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-3 sm:p-4 flex-1 flex flex-col">
        {/* SKU - 모바일에서는 숨김 */}
        <div className="text-caption text-apple-text-secondary mb-1 hidden sm:block">SKU: {product.sku || 'N/A'}</div>

        <div className="flex justify-between items-start mb-2">
          <h3 className="text-body-sm font-sf-pro text-apple-text-primary line-clamp-2 flex-1 pr-2 leading-tight">{product.name}</h3>
          <span className="text-caption text-apple-accent font-medium hidden sm:block flex-shrink-0">{product.category}</span>
        </div>

        <p className="text-caption text-apple-text-secondary mb-3 line-clamp-2 hidden sm:block leading-relaxed">{product.description}</p>

        {/* 별점 & 주문 수 - 모바일에서는 간소화 */}
        <div className="flex items-center gap-2 mb-3 text-caption text-apple-text-secondary">
          {avgRating > 0 ? (
            <>
              <div className="flex items-center gap-1">
                <Star size={10} fill="currentColor" className="text-yellow-500" />
                <span className="text-caption">{avgRating}</span>
              </div>
              <span className="hidden sm:inline">•</span>
            </>
          ) : null}
          <span className="text-caption">{totalOrders} orders</span>
        </div>

        <div className="space-y-1 mb-4">
          <div className="flex justify-between">
            <span className="text-caption text-apple-text-secondary">From:</span>
            <span className="text-body-sm font-sf-pro text-apple-text-primary">${unitPrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-caption text-apple-text-secondary">Best:</span>
            <span className="text-body-sm font-sf-pro text-green-600">${lowestPrice.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex gap-2 mt-auto">
          <Link 
            to={`/product/${product.id}`}
            className="flex-1 apple-button-secondary text-caption py-2 text-center"
          >
            View Detail
          </Link>
          <button 
            onClick={handleAddToCart}
            disabled={isAddingToCart}
            className={`apple-button-secondary p-2 rounded-apple apple-hover transition-all duration-200 ${
              isAddingToCart ? 'opacity-50 cursor-not-allowed' : ''
            } ${addedToCart ? 'bg-green-500 text-white' : ''}`}
            title={addedToCart ? "Added to Cart!" : "Add to Cart"}
          >
            {addedToCart ? (
              <span className="text-xs">✓</span>
            ) : (
              <ShoppingCart size={14} className="sm:w-4 sm:h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;